// Part-2 reviewer-profile ("evaluate") form — July-8 FR-L3: EVERY proposal goes through this
// form, and it opens immediately after Propose/Claim (the proposal stays `incomplete`, hidden
// from AE QC per FR-K15, until this is submitted). July-7 FR-K17: fields auto-populate from what
// we already know and stay fully editable. July-8 Bucket 2: institutional profile + justification
// helper explaining the invite-email sentence.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deriveJustification, type ProposalView } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { Button, Field, Input, Modal } from "../../components/ui.js";

export function EvaluateModal({ proposal, onClose }: { proposal: ProposalView; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState({
    subdiscipline: proposal.subdiscipline ?? "",
    bestMatchPaperTitle: proposal.bestMatchPaperTitle ?? "",
    secondaryAffiliation: proposal.secondaryAffiliation ?? "",
    highestDegree: proposal.highestDegree ?? "",
    title: proposal.title ?? "",
    linkToProfile: proposal.linkToProfile ?? "",
    publicationLink: proposal.linkToPublications ?? proposal.pubMedLink ?? "",
  });
  const save = useMutation({
    mutationFn: () => api.post(`/proposals/${proposal.id}/evaluate`, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      qc.invalidateQueries({ queryKey: ["qc"] });
      onClose();
    },
  });
  const justification = f.subdiscipline && f.bestMatchPaperTitle ? deriveJustification(f.subdiscipline, f.bestMatchPaperTitle) : "";

  return (
    <Modal open onClose={onClose} title={`Reviewer profile (Part 2) — ${proposal.firstName} ${proposal.lastName}`}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Complete the reviewer profile to finish this proposal — it reaches quality check only once this is submitted.
        </p>
        <Field label="Sub-discipline (required)">
          <Input value={f.subdiscipline} onChange={(e) => setF({ ...f, subdiscipline: e.target.value })} required />
        </Field>
        <Field label="Best-match paper title (required)">
          <Input value={f.bestMatchPaperTitle} onChange={(e) => setF({ ...f, bestMatchPaperTitle: e.target.value })} required />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Highest degree">
            <Input value={f.highestDegree} onChange={(e) => setF({ ...f, highestDegree: e.target.value })} />
          </Field>
          <Field label="Title">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </Field>
        </div>
        <Field label="Institutional profile (link)">
          <Input
            value={f.linkToProfile}
            onChange={(e) => setF({ ...f, linkToProfile: e.target.value })}
            placeholder="https://… faculty or lab page"
          />
        </Field>
        <Field label="Publication / PubMed link">
          <Input value={f.publicationLink} onChange={(e) => setF({ ...f, publicationLink: e.target.value })} />
        </Field>
        {justification && (
          <div className="rounded-md bg-slate-50 p-2 text-sm text-slate-600">
            <span className="text-xs font-semibold text-slate-500">Justification preview</span>
            <p className="mt-1 text-xs text-slate-500">
              This sentence is copied directly into the reviewer invitation email — read it aloud and fix anything that sounds awkward before submitting.
            </p>
            <p className="mt-1 italic">{justification}</p>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Finish later
          </Button>
          <Button onClick={() => save.mutate()} disabled={!f.subdiscipline || !f.bestMatchPaperTitle || save.isPending}>
            {save.isPending ? "Submitting…" : "Submit reviewer"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
